import React from 'react';
import { Typography, Tooltip, Tag } from 'antd';
import { 
  UserAddOutlined, 
  UserDeleteOutlined, 
  InfoCircleOutlined, 
  LockOutlined, 
  UnlockOutlined,
  EditOutlined 
} from '@ant-design/icons';
import { Message } from '../../types/chat';
import { formatMessageTime, formatFullDate } from './utils';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface ServiceMessageProps {
  message: Message;
}

/**
 * Component for displaying service messages like room creation, member join/leave, etc.
 */
const ServiceMessage: React.FC<ServiceMessageProps> = ({ message }) => {
  const { t } = useTranslation();
  let content = '';
  
  // Get content from the message
  if (typeof message.content === 'object' && message.content.body) {
    content = message.content.body;
  } else if (typeof message.content === 'string') {
    content = message.content;
  }
  
  // Determine icon and style based on message type
  let icon = <InfoCircleOutlined />;
  let messageStyle = {};
  let tagColor = '';
  
  switch (message.type) {
    case 'm.room.create':
      icon = <InfoCircleOutlined />;
      messageStyle = { backgroundColor: 'rgba(0, 128, 0, 0.05)' };
      tagColor = 'green';
      break;
    case 'm.room.member':
      // Check for join/leave/invite based on content
      const contentObj = typeof message.content === 'object' ? message.content : null;
      const membership = contentObj?.membership;
      
      if (membership === 'join' || content.includes(t('serviceMessage.join'))) {
        icon = <UserAddOutlined />;
        tagColor = 'green';
      } else if (membership === 'leave' || content.includes(t('serviceMessage.leave'))) {
        icon = <UserDeleteOutlined />;
        tagColor = 'red';
      } else {
        icon = <UserAddOutlined />;
        tagColor = 'blue';
      }
      
      messageStyle = { backgroundColor: 'rgba(64, 128, 255, 0.05)' };
      break;
    case 'm.room.name':
    case 'm.room.topic':
      // Room name/topic change
      icon = <EditOutlined />;
      messageStyle = { backgroundColor: 'rgba(128, 0, 128, 0.05)' };
      tagColor = 'purple';
      break;
    case 'm.room.history_visibility':
    case 'm.room.join_rules':
    case 'm.room.guest_access':
      // Permission changes
      const isRestricted = content.includes(t('serviceMessage.restricted'));
      icon = isRestricted ? <LockOutlined /> : <UnlockOutlined />;
      messageStyle = { backgroundColor: 'rgba(255, 165, 0, 0.05)' };
      tagColor = isRestricted ? 'orange' : 'green';
      break;
    default:
      messageStyle = { backgroundColor: 'rgba(0, 0, 0, 0.03)' };
      tagColor = 'default';
  }
  
  return (
    <div className="service-message" style={messageStyle}>
      <Tag color={tagColor} icon={icon} style={{ marginRight: 8 }} />
      <Text type="secondary" italic>
        {content}
        <span className="service-message-time">
          <Tooltip title={formatFullDate(message.created)}>
            {formatMessageTime(message.created)}
          </Tooltip>
        </span>
      </Text>
    </div>
  );
};

export default ServiceMessage; 